export const CAMPAIGN_LIFETIME_MS = 30 * 24 * 60 * 60 * 1000;

export interface AttributionCampaign {
  readonly values: Readonly<Record<string, string>>;
  readonly landingPath: string;
  readonly landedAt: number;
}

export interface CampaignLocation {
  readonly search: string;
  readonly pathname: string;
}

const pathOnly = (path: string) => path.split(/[?#]/)[0].slice(0, 512);
const scalar = (value: string) => Array.from(value).slice(0, 256).join('');

/* Separate from the legacy store: campaign replacement and expiry must never
   change what the legacy analytics and own-form paths already read. */
export const createAttributionCampaignStore = (
  storage: () => Storage | undefined = () => typeof window === 'undefined' ? undefined : window.localStorage,
  clock: () => number = Date.now
) => {
  const memory = new Map<string, AttributionCampaign>();
  const unpersisted = new Set<string>();
  const lastLocations = new Map<string, string>();
  const key = (eventId: string) => `UG_ATTRIBUTION_CAMPAIGN_${eventId}`;

  const read = (eventId: string): AttributionCampaign | undefined => {
    const now = clock();
    const valid = (campaign: AttributionCampaign | undefined): campaign is AttributionCampaign =>
      !!campaign && typeof campaign === 'object' && Number.isFinite(campaign.landedAt) && campaign.landedAt <= now &&
      now - campaign.landedAt < CAMPAIGN_LIFETIME_MS && typeof campaign.landingPath === 'string' &&
      !!campaign.values && typeof campaign.values === 'object' &&
      Object.entries(campaign.values).every(([name, value]) => name.startsWith('utm_') && typeof value === 'string');
    let campaign = memory.get(eventId);
    try {
      const target = storage();
      if (target) {
        const serialized = target.getItem(key(eventId));
        let saved: AttributionCampaign | undefined;
        try { saved = JSON.parse(serialized || 'null') || undefined; } catch { /* Treat corrupt saved state as absent. */ }
        if (!unpersisted.has(eventId) || !valid(campaign) || (valid(saved) && saved.landedAt > campaign.landedAt)) {
          campaign = saved;
          unpersisted.delete(eventId);
        }
      }
    } catch { /* Blocked storage keeps this tab's current campaign available. */ }
    if (!valid(campaign)) {
      memory.delete(eventId);
      unpersisted.delete(eventId);
      return undefined;
    }
    /* Reads never delete shared storage: another tab can replace it between
       a comparison and removal. Expired/corrupt records are ignored instead. */
    memory.set(eventId, campaign);
    return campaign;
  };

  const capture = (eventId: string, location: CampaignLocation): AttributionCampaign | undefined => {
    if (!eventId) return undefined;
    const signature = location.pathname + location.search;
    const parameters = new URLSearchParams(location.search);
    const names = Array.from(new Set(Array.from(parameters.keys()).filter(name => name.startsWith('utm_'))));
    const newLanding = lastLocations.get(eventId) !== signature;
    lastLocations.set(eventId, signature);
    if (!names.length || !newLanding) return read(eventId);
    const values: Record<string, string> = {};
    for (const name of names) {
      const entries = parameters.getAll(name);
      if (entries.length === 1) values[name] = scalar(entries[0]);
    }
    const campaign = Object.freeze({ values: Object.freeze(values), landingPath: pathOnly(location.pathname), landedAt: clock() });
    memory.set(eventId, campaign);
    unpersisted.add(eventId);
    try {
      const target = storage();
      if (target) {
        target.setItem(key(eventId), JSON.stringify(campaign));
        unpersisted.delete(eventId);
      }
    } catch { /* Use this landing in memory. */ }
    return campaign;
  };

  return { read, capture };
};

export const attributionCampaignStore = createAttributionCampaignStore();

export const captureAttributionCampaign = (eventId: string, location?: CampaignLocation) => {
  const current = location ?? (typeof window !== 'undefined' ? window.location : undefined);
  return current ? attributionCampaignStore.capture(eventId, current) : undefined;
};
